import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  Security,
  Group,
  FileDownload,
  Receipt,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
} from "@mui/icons-material";
import { useGetDashboardStats } from "../../../../../stores/Taxation/taxation.queries";

const FiskalyDashboard: React.FC = () => {
  const {
    data: stats,
    isLoading,
    error,
    isRefetching,
  } = useGetDashboardStats();

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Fehler beim Laden der Dashboard-Daten
      </Alert>
    );
  }

  return (
    <Box sx={{ height: "100%", overflow: "auto", pb: 2 }}>
      {/* Header with refresh indicator */}
      <Box sx={{ mb: 2, position: "relative" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 0.5,
            fontWeight: 600,
            color: "#003059",
          }}
        >
          Fiskaly Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Systemübersicht und Statistiken
        </Typography>
        {isRefetching && (
          <LinearProgress
            sx={{ position: "absolute", bottom: -8, left: 0, right: 0 }}
          />
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Lade Dashboard-Daten...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Main Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* TSS Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Security sx={{ fontSize: 24, color: "#16a085", mr: 1.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    TSS
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: "bold", color: "#16a085", mb: 0.5 }}
                  >
                    {stats?.totalTss || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Technische Sicherheitseinrichtungen
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Clients Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Group sx={{ fontSize: 24, color: "#3498db", mr: 1.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Clients
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: "bold", color: "#3498db", mb: 0.5 }}
                  >
                    {stats?.totalClients || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Registrierte Clients
                  </Typography>
                </Box>

                {stats?.clientStats?.byState && (
                  <Box sx={{ bgcolor: "#e3f2fd", p: 2, borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Registriert
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {stats.clientStats.byState.REGISTERED || 0}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Deregistriert
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {stats.clientStats.byState.DEREGISTERED || 0}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Exports Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <FileDownload
                    sx={{ fontSize: 24, color: "#9b59b6", mr: 1.5 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Exports
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: "bold", color: "#9b59b6", mb: 0.5 }}
                  >
                    {stats?.totalExports || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Durchgeführte Exporte
                  </Typography>
                </Box>

                {stats?.exportStats?.byStatus &&
                  Object.keys(stats.exportStats.byStatus).length > 0 && (
                    <Box sx={{ bgcolor: "#f3e5f5", p: 2, borderRadius: 2 }}>
                      {Object.entries(stats.exportStats.byStatus).map(
                        ([status, count]) => (
                          <Box
                            key={status}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {status}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#9b59b6" }}
                            >
                              {count}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  )}
              </Paper>
            </Grid>
          </Grid>

          {/* Detailed Statistics Section */}
          <Grid container spacing={2}>
            {/* TSS Status Breakdown */}
            {stats?.tssStats?.byState &&
              Object.keys(stats.tssStats.byState).length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Security
                        sx={{ fontSize: 24, color: "#16a085", mr: 1.5 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        TSS Status-Übersicht
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {Object.entries(stats.tssStats.byState).map(
                        ([state, count]) => {
                          const getStateConfig = (state: string) => {
                            switch (state) {
                              case "INITIALIZED":
                                return {
                                  icon: <CheckCircle />,
                                  color: "#4caf50",
                                  label: "Initialisiert",
                                };
                              case "UNINITIALIZED":
                                return {
                                  icon: <Warning />,
                                  color: "#ff9800",
                                  label: "Nicht initialisiert",
                                };
                              case "DISABLED":
                                return {
                                  icon: <ErrorIcon />,
                                  color: "#f44336",
                                  label: "Deaktiviert",
                                };
                              case "CREATED":
                                return {
                                  icon: <Info />,
                                  color: "#2196f3",
                                  label: "Erstellt",
                                };
                              default:
                                return {
                                  icon: <Info />,
                                  color: "#9e9e9e",
                                  label: state,
                                };
                            }
                          };

                          const config = getStateConfig(state);
                          const percentage = stats.totalTss
                            ? ((count as number) / stats.totalTss) * 100
                            : 0;

                          return (
                            <Box key={state}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box sx={{ color: config.color }}>
                                    {config.icon}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {config.label}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {count}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({percentage.toFixed(0)}%)
                                  </Typography>
                                </Box>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: `${config.color}20`,
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: config.color,
                                    borderRadius: 4,
                                  },
                                }}
                              />
                            </Box>
                          );
                        }
                      )}
                    </Box>
                  </Paper>
                </Grid>
              )}

            {/* Transaction Activity Summary */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Receipt sx={{ fontSize: 24, color: "#3498db", mr: 1.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Transaktionsübersicht
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    variant="h1"
                    sx={{ fontWeight: "bold", color: "#3498db", mb: 1 }}
                  >
                    {stats?.tssStats?.totalTransactions?.toLocaleString(
                      "de-DE"
                    ) || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Gesamte Transaktionen
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default FiskalyDashboard;
